import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Button, Table, Divider, Row, Col, Tag, Typography } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Text } = Typography;

/**
 * ListDetailTables component for displaying a list with associated detail tables
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing list and details
 * @param {Array} props.data.list - List of policies with their status
 * @param {Array} props.data.details - Detail data for each policy
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const ListDetailTables = ({
  data,
  title = "Review Results",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // Destructure the data
  const { list = [], details = [] } = data || {};
  
  // State to track which detail is being viewed
  const [activeDetailIndex, setActiveDetailIndex] = useState(null);
  
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  

  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????

  // Handler for showing details
  const handleShowDetails = (index) => {
    setActiveDetailIndex(index === activeDetailIndex ? null : index);
  };
  
  // Function to render text/markdown content
  const renderTextContent = (content) => {
    if (!content) return <Text type="secondary">No content available</Text>;
    
    // Handle markdown formatting
    const formatMarkdown = (text) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
        .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic text
        .replace(/\n/g, '<br/>') // Line breaks
        .replace(/^### (.*$)/gim, '<h3>$1</h3>') // H3 headers
        .replace(/^## (.*$)/gim, '<h2>$1</h2>') // H2 headers
        .replace(/^# (.*$)/gim, '<h1>$1</h1>'); // H1 headers
    };

    return (
      <div 
        style={{ 
          padding: '12px 16px',
          backgroundColor: '#f9f9f9',
          borderRadius: '6px',
          border: '1px solid #e8e8e8',
          fontSize: '18px',
          lineHeight: '1.6'
        }}
        dangerouslySetInnerHTML={{
          __html: formatMarkdown(content)
        }}
      />
    );
  };

  // Enhanced format the supporting evidence for both arrays and text/markdown
  const formatSupportingEvidence = (evidence) => {
    if (!evidence) return null;
    
    // Handle text/markdown content
    if (typeof evidence === 'string') {
      return renderTextContent(evidence);
    }
    
    // Handle array content (original table logic)
    if (!Array.isArray(evidence) || evidence.length === 0) return null;
    
    // Get the first item to determine columns dynamically
    const firstItem = evidence[0];
    if (!firstItem || typeof firstItem !== 'object') return null;
    
    // Generate columns dynamically from the data keys
    const columns = Object.keys(firstItem).map(key => ({
      title: key.toUpperCase().replace(/_/g, ' '), // Convert field name to title
      dataIndex: key,
      key: key,
      onHeaderCell: () => ({
        style: {
          backgroundColor: '#f0f0f0', // Slightly darker background
          fontWeight: 'bold',         // Bold font
          verticalAlign: 'top',       // Top alignment
          padding: '8px',             // Add some padding
          fontSize: '16px', 
          borderBottom: '2px solid #d9d9d9' // Slightly thicker bottom border
        }
      }),
      render: (text) => {
        // Handle null/undefined values
        if (text === null || text === undefined) return '-';
        // Handle boolean values
        if (typeof text === 'boolean') return text ? 'Yes' : 'No';
        return String(text);
      }
    }));
    
    return (
      <Table
        columns={columns}
        dataSource={evidence.map((item, i) => ({ ...item, key: i }))}
        pagination={false}
        size="small"
        scroll={{ x: true }} // Allow horizontal scrolling for wide tables
        components={{
          header: {
            cell: (props) => (
              <th 
                {...props} 
                style={{
                  ...props.style,
                  backgroundColor: '#f0f0f0',
                  fontWeight: 'bold',
                  verticalAlign: 'top',
                  textAlign: 'center'
                }}
              />
            )
          }
        }}
      />
    );
  };
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  // Button styles
  const buttonStyle = {
    backgroundColor: '#e0cbff',
    borderColor: '#c29fee',
    color: '#4d2c91',
    fontWeight: 'bold'
  };
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        className="custom-collapse"
        items={[
          {
            key: '1',
            label: (
              <div style={{ 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                {title}
              </div>
            ),
            children: (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Workflow Steps</div>
                  
                  {/* Display the list of policies */}
                  {list.map((item, index) => {
                    const [isPass, policyName, buttonText] = item;
                    
                    return (
                      <div 
                        key={index}
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}
                      >
                        <div>
                          {isPass ? (
                            <CheckOutlined style={{ color: 'green', marginRight: '8px' }} />
                          ) : (
                            <CloseOutlined style={{ color: 'red', marginRight: '8px' }} />
                          )}
                          <span>{policyName}</span>
                        </div>
                        <Button 
                          size="small"
                          style={buttonStyle}
                          onClick={() => handleShowDetails(index)}
                        >
                          Details
                        </Button>
                      </div>
                    );
                  })}
                </div>
                
                {/* Display the active policy details */}
                {activeDetailIndex !== null && details[activeDetailIndex] && (
                  <div>
                    
                    {details[activeDetailIndex].title && (
                      <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                        <strong>{details[activeDetailIndex].title}</strong>
                      </div>
                    )}
                    
                    {details[activeDetailIndex].validation && (
                      <div style={{ marginBottom: '8px', textAlign: 'center', fontSize: '24px'  }}>
                        <Tag color={details[activeDetailIndex].validation === 'Pass' ? 'green' : 'red'}>
                          {details[activeDetailIndex].validation}
                        </Tag>
                      </div>
                    )}
                    
                    {details[activeDetailIndex].explanation && (
                      <div style={{ marginBottom: '16px', fontSize: '24px' }}>
                        {details[activeDetailIndex].explanation}
                      </div>
                    )}
                    
                    {details[activeDetailIndex].supporting_evidence && (
                      <div style={{ marginBottom: '16px', fontSize: '16px' }}>
                        {formatSupportingEvidence(details[activeDetailIndex].supporting_evidence)}
                      </div>
                    )}
                  </div>
                )}
                {/* Version navigation */}
                {/*
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      size="small" 
                      icon={<LeftOutlined />}
                      style={buttonStyle}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </Button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <Button 
                      size="small" 
                      icon={<RightOutlined />}
                      style={buttonStyle}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
                */}
              </>
            )
          }
        ]}
      />
    </div>
  );
};

export default ListDetailTables;